import { format } from 'date-fns'
import _ from 'lodash'
import moment from 'moment'
import queryString from 'query-string'
import { toast } from 'react-toastify'

import { mainText } from 'assets/styles/variables'
import type { ApiClient } from 'clients/yogi-api'
import { initialChartStoreState } from 'features/charts/store'
import type { DashboardControlsConfig } from 'features/dashboard/types'
import {
  AppliedFilterOption,
  AppliedFilterOptionSelect,
  type DateRange,
} from 'features/filters/types'
import {
  formatDateToUTCEndOfDay,
  formatDateToUTCMidnight,
  parseUriPayload,
} from 'features/filters/utils/helpers'
import { detectCurrentRoute } from 'routes/detection'
import {
  DateWindows,
  FeedbackControlsConfig,
  Filters,
  KeywordsControlsConfig,
  PostDetails,
} from 'types/project'

// =============================================================================
// Type Definitions
// =============================================================================

interface GlobalViewFilter {
  field: string
  values: string[]
}

interface FilterContext {
  filterValues: {
    globalViewFilters: GlobalViewFilter[]
  }
}

interface ReportBody {
  filter_context: FilterContext
}

interface NewStyleFilter {
  values: AppliedFilterOption[]
  searchQuery: string[]
  searchCondition: string[]
}

export const HIGHLIGHT_TAG_ROOT = 'Yogi-highlight'
export const SEARCH_HIGHLIGHT_COLOR = 'rgba(241, 197, 95, 100)'
export const PROMOTION_ENABLED_HIGHLIGHT_COLOR = 'rgba(187,241,176,2)'
export const TRANSPARENT_COLOR = 'rgba(0,0,0,0)'
export const DEFAULT_CHART_FONT_SIZE = 'medium'

// Route URL helpers (SCREEN_STATE_PAGES, isScreenStatePage, getScreenIdFromPath,
// buildPageUrl, buildProjectPageUrl, buildProjectCustomDashboardPath,
// buildDbSearchUrl, getCurrentRouteName, detectCurrentRoute) have been moved
// to the routes/ module. Import from 'routes/builders' or 'routes/detection'.

export const emptyFilters: Filters = {
  values: [],
  searchCondition: [],
  searchQuery: [],
  search_terms_logical: null,
}

export const postToCsv = (post?: PostDetails): string => {
  if (!post) return ''
  let headerRow: string | null = null
  let contentRow: string | null = null
  for (const [key, value] of Object.entries(post)) {
    if (value && key !== 'post_uuid') {
      const formattedKey = key
        .split('_')
        .map((item) => (item[0]?.toUpperCase() ?? '') + item.slice(1))
        .join(' ')
      let resKey = ''
      switch (formattedKey) {
        case 'Create Time':
          resKey = 'Date'
          break
        case 'Product Hierarchy':
          resKey = 'Product'
          break
        case 'Product':
          resKey = 'Item'
          break
        case 'Product Group':
          resKey = 'Item Groups'
          break
        case 'Score':
          resKey = 'Rating'
          break
        default:
          resKey = formattedKey
      }
      const resValue =
        resKey === 'Date'
          ? format(new Date(+(value as number) * 1000), 'MM/dd/yyyy')
          : value

      if (typeof value == 'object' && value !== null) {
        Object.entries(value).forEach(([objKey, objValue]) => {
          headerRow = headerRow ? headerRow + '\t' + objKey : '' + objKey
          contentRow = contentRow ? contentRow + '\t' + objValue : '' + objValue
        })
      } else {
        headerRow = headerRow ? headerRow + '\t' + resKey : '' + resKey
        contentRow = contentRow ? contentRow + '\t' + resValue : '' + resValue
      }
    }
  }
  return headerRow + '\n' + contentRow
}

const getQueryParamByKey = (key: string): string | null => {
  const searchParams = new URLSearchParams(window.location.search)
  return searchParams.get(key)
}

export const getReport = async (
  client: ApiClient,
  body: ReportBody,
  proj_uuid: string,
): Promise<void> => {
  const currentRoute = detectCurrentRoute(window.location.pathname)

  // for some reasons unkown for me there is not always chart_id is pased to
  if (currentRoute.isCharts) {
    const globalFilters = body.filter_context.filterValues.globalViewFilters
    const chartIdFilter = globalFilters.find(
      (filter: GlobalViewFilter) => filter.field === 'chart_id',
    )
    if (chartIdFilter?.values) {
      chartIdFilter.values = [getQueryParamByKey('chart_id') ?? '']
    } else {
      globalFilters.push({
        field: 'chart_id',
        values: [getQueryParamByKey('chart_id') ?? ''],
      })
    }

    const postDataFilter = globalFilters.find(
      (filter: GlobalViewFilter) => filter.field === 'post_data',
    )
    if (postDataFilter?.values) {
      postDataFilter.values = [getQueryParamByKey('post_data') ?? '']
    } else {
      globalFilters.push({
        field: 'post_data',
        values: [getQueryParamByKey('post_data') ?? ''],
      })
    }
  }

  toast.success('Your export will be sent to your email!')
  return client
    .post(
      '/dashboard/generate-report',
      { body },
      { params: { proj_uuid }, responseType: 'blob', timeout: 180000 },
    )
    .then(() => undefined)
    .catch(() => {
      toast.error(
        'Something wrong happened on our side. Mail will not be sent :(',
      )
    })
}

export const generateAppliedOptionSelect = (
  field: string,
  values: string[],
): AppliedFilterOptionSelect => ({ field, values }) as AppliedFilterOptionSelect

export const trimText = (text: string, maxLength: number): string =>
  text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text

export const getComparativeIndex = (value: string): number => {
  if (value) {
    const valueParts = value.split('_')
    const lastPart = Number(valueParts[valueParts.length - 1])
    if (Number.isInteger(lastPart)) {
      return lastPart // not to break everything
    }
  }
  return -1
}

export const getFilterValueName = (value: string): string => {
  return value?.slice(0, -2)
}

export function parseUriParams(url: string): Array<AppliedFilterOption> {
  const parsedUrl = queryString.parse(url)
  return parseUriPayload(
    parsedUrl as Record<string, string | string[] | null | undefined>,
  )
}

function stringToBoolean(string: string): string | boolean {
  if (string && typeof string === 'string') {
    if (string.toLowerCase() === 'true') {
      return true
    }
    if (string.toLowerCase() === 'false') {
      return false
    }
  }
  return string
}

export const hydrateSavedViewURL = (
  queryString: string,
  updateFilters: (index: number, filter: NewStyleFilter) => void,
  setDashboardControls: (controls: DashboardControlsConfig[]) => void,
  setFeedbackControls: (controls: FeedbackControlsConfig[]) => void,
  setKeywordsControls: (controls: KeywordsControlsConfig[]) => void,
): void => {
  const urlParams = new URLSearchParams(queryString)

  const filter_values = parseUriParams(queryString).find(
    (el) => el.field === 'filter_values',
  ) as AppliedFilterOptionSelect | undefined
  const newStyleFilters: NewStyleFilter[] = []
  if (filter_values) {
    // this is promising for parsing the payload - odd format for what i'm doing tho
    const parsedPayload = parseUriPayload(
      filter_values?.values as unknown as Record<string, string>,
    )
    // todo transform filter_values into filterValues format

    parsedPayload.sort(
      (a, b) => getComparativeIndex(a.field) - getComparativeIndex(b.field),
    )

    parsedPayload.forEach((el: AppliedFilterOption) => {
      const cI = getComparativeIndex(el.field)

      if (!newStyleFilters[cI]) {
        newStyleFilters.push({
          values: [],
          searchQuery: [],
          searchCondition: [],
        })
      }
      newStyleFilters[cI]!.values.push({
        ...el,
        field: getFilterValueName(el.field),
      })
    })
  }

  // populate search query and search condition
  const searchQueries = parseUriParams(queryString).filter((el) =>
    el.field.match(/search_query_\d/),
  ) as AppliedFilterOptionSelect[]
  searchQueries.forEach((el: AppliedFilterOptionSelect) => {
    const cI = getComparativeIndex(el.field)

    if (!newStyleFilters[cI]) {
      newStyleFilters.push({
        values: [],
        searchQuery: [],
        searchCondition: [],
      })
    }
    newStyleFilters[cI]!.searchQuery = el.values as string[]
  })

  const searchConditions = parseUriParams(queryString).filter((el) =>
    el.field.match(/search_condition_\d/),
  ) as AppliedFilterOptionSelect[]
  searchConditions.forEach((el: AppliedFilterOptionSelect) => {
    const cI = getComparativeIndex(el.field)

    if (!newStyleFilters[cI]) {
      newStyleFilters.push({
        values: [],
        searchQuery: [],
        searchCondition: [],
      })
    }
    newStyleFilters[cI]!.searchCondition = el.values as string[]
  })

  //so, if comparative but no values on one side
  newStyleFilters.forEach((el: NewStyleFilter, index: number) => {
    updateFilters(index, el)
  })

  // DASHBOARD CONTROLS
  const dashboardObj: Record<string, string> = {}
  for (const [key, value] of urlParams.entries()) {
    dashboardObj[_.camelCase(key)] = value
  }

  const initialDash = initialChartStoreState.dashboardControls[0]!

  const dashboardObjWithInit: Record<string, string | number | boolean | null> =
    {}
  for (const [key] of Object.entries(initialDash)) {
    const camelKey = _.camelCase(key)
    const dashVal = dashboardObj[camelKey]
    dashboardObjWithInit[camelKey] =
      (dashVal != null ? stringToBoolean(dashVal) : undefined) ??
      (
        initialDash as unknown as Record<
          string,
          string | number | boolean | null
        >
      )[camelKey] ??
      null
  }
  setDashboardControls([
    dashboardObjWithInit as unknown as DashboardControlsConfig,
  ])

  // FEEDBACK PAGE
  const feedbackFilters = parseUriParams(queryString).filter((el) =>
    el.field.match(/feedback_filter_\d/),
  ) as AppliedFilterOptionSelect[]
  if (feedbackFilters?.length) {
    const newFeedbackFilters: Record<string, AppliedFilterOptionSelect>[] = []

    feedbackFilters.forEach((el: AppliedFilterOptionSelect) => {
      const cI = getComparativeIndex(el.field)
      newFeedbackFilters[cI] = el.values as unknown as Record<
        string,
        AppliedFilterOptionSelect
      >
    })

    const formattedNewFeedbackFilters = newFeedbackFilters.map(
      (el: Record<string, AppliedFilterOptionSelect>) => {
        return {
          order:
            (el['order']?.values as string[])?.[0]?.split(/_\d/)[0] ?? null,
          orderField:
            (el['order_field']?.values as string[])?.[0]?.split(/_\d/)[0] ??
            null,
          page: 1,
          pageSize: 20,
        } as FeedbackControlsConfig
      },
    )
    setFeedbackControls(formattedNewFeedbackFilters)
  }

  // KEYWORDS PAGE
  const keywordsFilters = parseUriParams(queryString).filter((el) =>
    el.field.match(/keywords_filter_\d/),
  ) as AppliedFilterOptionSelect[]
  if (keywordsFilters?.length) {
    const newKeywordsFilters: Record<string, AppliedFilterOptionSelect>[] = []
    keywordsFilters.forEach((el: AppliedFilterOptionSelect) => {
      const cI = getComparativeIndex(el.field)
      newKeywordsFilters[cI] = el.values as unknown as Record<
        string,
        AppliedFilterOptionSelect
      >
    })

    const formattedNewKeywordsFilters = newKeywordsFilters.map(
      (el: Record<string, AppliedFilterOptionSelect>) => {
        const windowSizeVal = (el['window_size']?.values as string[])?.[0]
        return {
          searchTerm: (el['search_term']?.values as string[])?.[0] ?? null,
          stopWords: (el['stop_words']?.values as string[]) ?? null,
          windowSize: windowSizeVal != null ? Number(windowSizeVal) : null,
        } as KeywordsControlsConfig
      },
    )
    setKeywordsControls(formattedNewKeywordsFilters)
  }
}

export const findDateWindow = (
  dateWindows: DateWindows,
  min: number | undefined,
  max: number | undefined,
): DateRange | undefined | null => {
  if (!dateWindows?.['allDates'] || !min || !max) return null
  return dateWindows['allDates'].find(
    (date: DateRange) =>
      formatDateToUTCMidnight(
        moment.unix(JSON.parse(date.value).min),
      ).unix() === formatDateToUTCMidnight(moment.unix(min)).unix() &&
      formatDateToUTCEndOfDay(
        moment.unix(JSON.parse(date.value).max),
      ).unix() === formatDateToUTCEndOfDay(moment.unix(max)).unix(),
  )
}
export function pickTextColorBasedOnBgColorAdvanced(bgColor: string): string {
  const lightColor = '#ffffff'
  const darkColor = mainText
  try {
    const rgb = bgColor
      .substring(4, bgColor.length - 1)
      .replace(/ /g, '')
      .split(',')
    const isHex = bgColor.charAt(0) === '#'
    const color = isHex ? bgColor.substring(1, 7) : bgColor
    const r = isHex ? parseInt(color.substring(0, 2), 16) : Number(rgb[0]) // hexToR
    const g = isHex ? parseInt(color.substring(2, 4), 16) : Number(rgb[1]) // hexToG
    const b = isHex ? parseInt(color.substring(4, 6), 16) : Number(rgb[2]) // hexToB
    const uicolors = [r / 255, g / 255, b / 255]
    const c = uicolors.map((col) => {
      if (col <= 0.03928) {
        return col / 12.92
      }
      return Math.pow((col + 0.055) / 1.055, 2.4)
    })
    const L = 0.2126 * (c[0] ?? 0) + 0.7152 * (c[1] ?? 0) + 0.0722 * (c[2] ?? 0)
    return L > 0.25 ? darkColor : lightColor
  } catch (_e) {
    return lightColor
  }
}
export function pickTextColorBasedOnBgColorSimple(bgColor: string): string {
  const lightColor = '#ffffff'
  const darkColor = mainText
  const color = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor
  const r = parseInt(color.substring(0, 2), 16) // hexToR
  const g = parseInt(color.substring(2, 4), 16) // hexToG
  const b = parseInt(color.substring(4, 6), 16) // hexToB
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? darkColor : lightColor
}

export function getFromLS(key: string): unknown {
  let ls: Record<string, unknown> = {}
  if (global.localStorage) {
    try {
      ls = JSON.parse(global.localStorage.getItem('rgl-8') ?? '{}') || {}
    } catch (_e) {
      /*Ignore*/
    }
  }
  return ls[key]
}

export function saveToLS(key: string, value: unknown): void {
  if (global.localStorage) {
    const existingData = global.localStorage.getItem('rgl-8')
    const data: Record<string, unknown> = existingData
      ? JSON.parse(existingData)
      : {}
    data[key] = value
    global.localStorage.setItem('rgl-8', JSON.stringify(data))
  }
}

export const stringToColour = (str: string): string => {
  let hash = 0
  str.split('').forEach((char) => {
    hash = char.charCodeAt(0) + ((hash << 5) - hash)
  })
  let colour = '#'
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff
    colour += value.toString(16).padStart(2, '0')
  }
  return colour
}

export const truncateStringFieldToBytes = (
  str: string,
  maxLength = 255,
): string =>
  new TextDecoder().decode(new TextEncoder().encode(str).slice(0, maxLength))

export const truncateStringFieldsForMixpanel = (
  obj: Record<string, unknown>,
  maxLength = 255,
): Record<string, unknown> => {
  // Mixpanel string fields max 255 bytes: https://docs.mixpanel.com/docs/data-structure/property-reference/data-type#string
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      result[key] = truncateStringFieldToBytes(value, maxLength)
    } else {
      result[key] = value
    }
  }
  return result
}
